import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Table from "@mui/material/Table";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import CircularProgress from "@mui/material/CircularProgress";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Invoice page components
import httpService from "services/http.service";


// Images
import logoCT from "assets/images/logo-ct.png";
import logoCTDark from "assets/images/logo-ct-dark.png";

// Material Dashboard 2 PRO React context
import { useMaterialUIController } from "context";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { makeStyles } from '@material-ui/core/styles';
import { Icon } from "@mui/material";

const useStyles = makeStyles({
  customTableHead: {
    display: 'table-header-group!important', // Use the correct CSS property without quotes
  },
});

function CustomerInvoiceDetail() {
  const { id } = useParams();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paying, setPaying] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const borderBottom = {
    borderBottom: ({ borders: { borderWidth }, palette: { light } }) =>
      `${borderWidth[1]} solid ${light.main}`,
  };

  const classes = useStyles();

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        setLoading(true);
        const data = await httpService.get(`/customer/invoices/${id}`);
        if (data?.success) {
          console.log('data',data)
          setInvoice(data?.data);
        } else {
          setError("Invoice not found or you do not have permission to view it.");
        }
      } catch (err) {
        console.error("Failed to fetch invoice:", err);
        setError("Invoice not found or you do not have permission to view it.");
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchInvoice();
  }, [id]);

  const handlePayment = async () => {
    try {
      setPaying(true);
      const response = await httpService.post(`/customer/invoices/${id}/checkout-session`);
      if (response && response.data) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error("Payment URL not received");
      }
    } catch (err) {
      console.error("Payment initiation failed:", err);
      alert("Could not initiate payment. Please try again.");
    } finally {
      setPaying(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      setDownloading(true);
      // The API for PDF download usually returns a blob or a redirect URL
      const response = await httpService.get(`/customer/invoices/${id}/pdf`, {
        responseType: "blob",
      });

      // If the response is a blob, create a download link
      if (response instanceof Blob || response.data instanceof Blob) {
        const blob = response.data || response;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `invoice_${invoice.invoice_number}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else if (response && response.url) {
        // If the API returns a URL to the PDF
        window.open(response.url, "_blank");
      } else {
        throw new Error("PDF download failed");
      }
    } catch (err) {
      console.error("PDF download failed:", err);
      alert("Could not download the invoice PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout stickyNavbar>
        <MDBox display="flex" justifyContent="center" alignItems="center" height="80vh">
          <CircularProgress />
        </MDBox>
      </DashboardLayout>
    );
  }

  if (error || !invoice) {
    return (
      <DashboardLayout stickyNavbar>
        <MDBox display="flex" justifyContent="center" alignItems="center" height="80vh">
          <MDTypography color="error">{error || "Invoice not found"}</MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  const outstandingAmount = ((invoice.total_amount_cents + invoice.shipping_cost_cents + invoice.tax_amount_cents) - invoice.paid_amount_cents) / 100;

  const totalAmount = (invoice.total_amount_cents + invoice.shipping_cost_cents + invoice.tax_amount_cents) / 100;

  return (
    <DashboardLayout stickyNavbar>
      <MDBox mt={{ xs: 4, md: 10 }} mb={{ xs: 4, md: 8 }}>
        <Grid container justifyContent="center">
          <Grid item xs={12} sm={10} md={8}>
            <Card>
              {/* Action Bar */}
              <MDBox p={3} display="flex" justifyContent="space-between" alignItems="center">
                <MDBox display="flex" gap={2}>
                  {/* <MDButton 
                    variant="text" 
                    color="info" 
                    startIcon={<Icon>arrow_back</Icon>}
                    onClick={() => navigate(-1)}
                  >
                    Back to Invoices
                  </MDButton> */}
                  <MDButton 
                    variant="outlined" 
                    color="info" 
                    startIcon={<Icon>download</Icon>}
                    disabled={downloading}
                    onClick={handleDownloadPdf}
                  >
                    {downloading ? "Downloading..." : "Download PDF"}
                  </MDButton>
                </MDBox>
                {invoice.total_amount_cents > invoice.paid_amount_cents && (
                  <MDButton 
                    variant="gradient" 
                    color="info" 
                    disabled={paying}
                    onClick={handlePayment}
                  >
                    {paying ? "Processing..." : "Pay Outstanding"}
                  </MDButton>
                )}
              </MDBox>

              {/* Invoice header */}
              <MDBox p={3}>
                <Grid container justifyContent="space-between">
                  <Grid item xs={12} md={4}>
                    <MDBox
                      component="img"
                      src={darkMode ? logoCT : logoCTDark}
                      width="25%"
                      p={1}
                      mb={1}
                    />
                    <MDTypography variant="h6" fontWeight="medium">
                      Rivio Inc.
                    </MDTypography>
                    <MDBox mt={1} mb={2}>
                      <MDTypography
                        display="block"
                        variant="body2"
                        color={darkMode ? "text" : "secondary"}
                      >
                        support@rivio.io
                      </MDTypography>
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} md={7} lg={3}>
                    <MDBox width="100%" textAlign={{ xs: "left", md: "right" }} mt={6}>
                      <MDBox mt={1}>
                        <MDTypography variant="h6" fontWeight="medium">
                          Billed to: {invoice.bill_to_name || "Customer"}
                        </MDTypography>
                      </MDBox>
                      <MDBox mb={1}>
                        <MDTypography variant="body2" color={darkMode ? "text" : "secondary"}>
                          {invoice.bill_to_address || "No address provided"}
                        </MDTypography>
                      </MDBox>
                    </MDBox>
                  </Grid>
                </Grid>
                <MDBox mt={{ xs: 5, md: 10 }}>
                  <Grid container justifyContent="space-between">
                    <Grid item xs={12} md={4}>
                      <MDTypography
                        variant="h6"
                        color={darkMode ? "text" : "secondary"}
                        fontWeight="regular"
                      >
                        Invoice no
                      </MDTypography>
                      <MDTypography variant="h5" fontWeight="medium">
                        {invoice.invoice_number}
                      </MDTypography>
                    </Grid>
                    <Grid item xs={12} md={7} lg={5}>
                      <MDBox
                        width="100%"
                        display="flex"
                        flexDirection={{ xs: "column", md: "row" }}
                        alignItems={{ xs: "flex-start", md: "center" }}
                        textAlign={{ xs: "left", md: "right" }}
                        mt={{ xs: 3, md: 0 }}
                      >
                        <MDBox width="50%">
                          <MDTypography
                            variant="h6"
                            color={darkMode ? "text" : "secondary"}
                            fontWeight="regular"
                          >
                            Invoice date:
                          </MDTypography>
                        </MDBox>
                        <MDBox width="50%">
                          <MDTypography variant="h6" fontWeight="medium">
                            {new Date(invoice.invoice_date).toLocaleDateString()}
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                      <MDBox
                        width="100%"
                        display="flex"
                        flexDirection={{ xs: "column", md: "row" }}
                        alignItems={{ xs: "flex-start", md: "center" }}
                        textAlign={{ xs: "left", md: "right" }}
                      >
                        <MDBox width="50%">
                          <MDTypography
                            variant="h6"
                            color={darkMode ? "text" : "secondary"}
                            fontWeight="regular"
                          >
                            Due date:
                          </MDTypography>
                        </MDBox>
                        <MDBox width="50%">
                          <MDTypography variant="h6" fontWeight="medium">
                            {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : "N/A"}
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                    </Grid>
                  </Grid>
                </MDBox>
              </MDBox>

              {/* Invoice table */}
              <MDBox p={3}>
                <MDBox width="100%" overflow="auto">
                  <Table>
                    <TableHead classes={{ root: classes.customTableHead }}>
                      <TableRow>
                        <TableCell py={1.5} px={1} textAlign="left" sx={borderBottom}>
                          <MDTypography variant="h6" color="text" fontWeight="medium">
                            Item
                          </MDTypography>
                        </TableCell>
                        <TableCell py={1.5} px={1} textAlign="left" sx={borderBottom}>
                          <MDTypography variant="h6" color="text" fontWeight="medium">
                            Qty
                          </MDTypography>
                        </TableCell>
                        <TableCell py={1.5} px={1} textAlign="left" sx={borderBottom}>
                          <MDTypography variant="h6" color="text" fontWeight="medium">
                            Rate
                          </MDTypography>
                        </TableCell>
                        <TableCell py={1.5} px={1} textAlign="left" sx={borderBottom}>
                          <MDTypography variant="h6" color="text" fontWeight="medium">
                            Amount
                          </MDTypography>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoice.items?.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell py={1.5} px={1} textAlign="left">
                            <MDTypography variant="body2" color={darkMode ? "text" : "secondary"}>
                              {item.description || "No description"}
                            </MDTypography>
                          </TableCell>
                          <TableCell py={1.5} px={1} textAlign="left">
                            <MDTypography variant="body2" color={darkMode ? "text" : "secondary"}>
                              {item.quantity}
                            </MDTypography>
                          </TableCell>
                          <TableCell py={1.5} px={1} textAlign="left">
                            <MDTypography variant="body2" color={darkMode ? "text" : "secondary"}>
                              ${(Number(item.unit_price_cents) / 100).toFixed(2)}
                            </MDTypography>
                          </TableCell>
                          <TableCell py={1.5} px={1} textAlign="left">
                            <MDTypography variant="body2" color={darkMode ? "text" : "secondary"}>
                              ${((Number(item.quantity) * Number(item.unit_price_cents)) / 100).toFixed(2)}
                            </MDTypography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </MDBox>
              </MDBox>

              {/* Invoice totals */}
              <MDBox p={3}>
                <Grid container justifyContent="flex-end">
                  <Grid item xs={12} md={4}>
                    <MDBox width="100%" display="flex" justifyContent="space-between" mb={1}>
                      <MDTypography variant="h6" color={darkMode ? "text" : "secondary"} fontWeight="regular">
                        Subtotal:
                      </MDTypography>
                      <MDTypography variant="h6" fontWeight="medium">
                        ${(invoice.subtotal_cents / 100).toFixed(2)}
                      </MDTypography>
                    </MDBox>
                    <MDBox width="100%" display="flex" justifyContent="space-between" mb={1}>
                      <MDTypography variant="h6" color={darkMode ? "text" : "secondary"} fontWeight="regular">
                        Shipping:
                      </MDTypography>
                      <MDTypography variant="h6" fontWeight="medium">
                        ${(invoice.shipping_cost_cents / 100).toFixed(2)}
                      </MDTypography>
                    </MDBox>
                    <MDBox width="100%" display="flex" justifyContent="space-between" mb={1}>
                      <MDTypography variant="h6" color={darkMode ? "text" : "secondary"} fontWeight="regular">
                        Tax:
                      </MDTypography>
                      <MDTypography variant="h6" fontWeight="medium">
                        ${(invoice.tax_amount_cents / 100).toFixed(2)}
                      </MDTypography>
                    </MDBox>
                    <MDBox width="100%" display="flex" justifyContent="space-between" mt={2} mb={2}>
                      <MDTypography variant="h5" fontWeight="medium">
                        Total:
                      </MDTypography>
                      <MDTypography variant="h5" fontWeight="medium">
                        ${(totalAmount).toFixed(2)}
                      </MDTypography>
                    </MDBox>
                    <MDBox width="100%" display="flex" justifyContent="space-between" mb={3}>
                      <MDTypography variant="h6" color="success" fontWeight="medium">
                        Paid:
                      </MDTypography>
                      <MDTypography variant="h6" color="success" fontWeight="medium">
                        ${(invoice.paid_amount_cents / 100).toFixed(2)}
                      </MDTypography>
                    </MDBox>
                    <MDBox width="100%" display="flex" justifyContent="space-between" mb={3}>
                      <MDTypography variant="h6" color="error" fontWeight="medium">
                        Outstanding:
                      </MDTypography>
                      <MDTypography variant="h6" color="error" fontWeight="medium">
                        ${outstandingAmount.toFixed(2)}
                      </MDTypography>
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default CustomerInvoiceDetail;